/* The Newsletter panel (Grow): one piece of writing, three places it goes.
 *
 * Her spec, 2026-09-17: "one module, one source piece of writing, three output
 * types... this is not three separate modules — it's one content object with a
 * `type` field." So the panel holds BLOG POSTS (the canonical full version) and
 * the CUTS made from them — an email and a Substack post — in one list, banded
 * by type, each cut naming the post it came from.
 *
 * Scripts' shape, because it is the same kind of thing: no Level 1, so the
 * Table and the Copy are two views of ONE list and keep the toggle (rule 9b);
 * Add asks the type first; settings behind the cog as a pop-up (rule 8); the
 * sections are the shared Block row (block.jsx); Brill is the shared Drafter
 * (drafter.jsx), one section or the whole piece.
 *
 * TWO THINGS ARE SPECIFIC TO THIS PANEL, and both are hers:
 *
 *   · THE CLAIMS CHECK. A blog post that is pure narrative is FLAGGED, in
 *     amber, on the record and in its chat — "a real tension with the archetype
 *     voice profile and needs a check, not an automatic override." It never
 *     blocks a save, never rewrites her, and she can ignore it forever.
 *   · MAKE A CUT. On a blog post with words in it, two buttons make the email
 *     and the Substack post. They create the cut EMPTY, with its own sections,
 *     and Brill writes it FROM the source in the chat — because a cut is "not
 *     just a trim", and a pre-filled copy of the blog is exactly the trim she
 *     said she did not want.
 *
 * NOTHING HERE SENDS OR PUBLISHES ANYTHING. Copy-out is the route that works
 * for everyone, GHL or not (her hard rule: "$97 a month, so not everyone is
 * going to want it"). The blog fields GHL's API asks for are stored so the
 * push needs no migration when she wires it up.
 */
import { useState } from "react";
import { Settings } from "lucide-react";
import {
  NEWSLETTER_TYPES, NEWSLETTER_STATE_LABEL, VARIANT_TYPES, addableBlockKinds,
  applyNewsletterBlockReply, blockKindDef, blockKindOf, blockName, blockState, blockWords,
  buildNewsletterBlockContext, buildNewsletterContext, claimsCheck, cutFromLabel, groupNewsletters,
  isSource, newsletterLabel, newsletterState, newsletterToText, newsletterWords,
  parseNewsletterBlockReply, parseWholeNewsletterReply, readMinutes, stripNewsletterBlock, typeDef,
} from "../../utils/newsletters";
import { pillarLabels } from "../../utils/pillars";
import { timeAgo } from "../../utils/admin";
import { buildMomentumContext } from "../../utils/momentumContext";
import { SortableItem, SortableList } from "../BrowseTable";
import { Block } from "./block";
import { Drafter } from "./drafter";
import { useRecordKeep } from "../../utils/pageChat";
import { SectionStatus } from "./fields";
import { B, STATUS_DOT, btnGhost, btnPrimary, input, nb, pDesc, pTitle } from "./tokens";
import { AddButton, Badge, Bread, ChatWithBrill, Menu2, Note, PageTitleField, SavedTick, StateDots, Status } from "./ui";
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { EmailCopy } from "./emailCopy";
import { MediaPickButton } from "./mediaPicker";
import { emailParts } from "../../utils/emailHtml";
import { isOnBlog } from "../../utils/blogPublish";

/* What this piece IS. A blog post is the source; a cut says what it was cut
   from, because that is the thing she cannot see by reading its name. */
export function NewsletterTypePill({ newsletter, newsletters = [] }) {
  const def = typeDef(newsletter?.type);
  const cut = cutFromLabel(newsletter, newsletters);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
      <Badge cls={isSource(newsletter) ? "trust" : "neutral"} text={def.label} />
      {cut ? <Badge cls="neutral" text={cut} /> : null}
    </span>
  );
}

const STATE_CLS = { done: "locked", draft: "draft", empty: "retired" };
function StateTag({ state }) {
  return <Status cls={STATE_CLS[state] || "draft"} text={NEWSLETTER_STATE_LABEL[state] || "Draft"} />;
}


/* ── The shell ────────────────────────────────────────────────────────────────
   Add asks WHICH OF THE THREE — and says, in the menu, that the two cuts are
   normally made FROM a post rather than started blank. She can still start one
   blank: an email that is not cut from anything is a real thing to write. */
export function NewsletterShell({ view, setView, error, onDismissError, menuOpen, setMenuOpen, onAdd, draftingBack = null, children }) {
  /* While she is drafting, at ANY width (2026-09-24 — until then a phone
     only): the panel's chrome gives the screen up to the chat, and
     MomentumBar stands down too. The drafting screen's one row is all that
     sits above the conversation. */
  const bare = !!draftingBack;
  return (
    <>
      {/* THE PANEL'S OWN HEAD STANDS DOWN ON A PHONE WHILE SHE DRAFTS. Her
          report, 2026-09-22: the crumb, the heading and the description were
          three of the rows that pushed the conversation down to four lines and
          the input below the fold. None of them answers a question she has
          while writing a block — the drafting screen's one row names where she
          is, and the chat card names what it is writing. */}
      {!bare && (<>
      <Bread text="Grow" />
      <h2 style={pTitle}>Newsletters</h2>
      <p style={pDesc}>
        One piece of writing, three places it goes. The BLOG POST is the full version — the one
        that has to stand on its own and say something quotable. The EMAIL and the SUBSTACK POST are
        cuts of it, each opening differently rather than saying less. Nothing here sends or posts
        anything: you write it, then copy it wherever it goes.
      </p>
      </>)}

      <MomentumBar view={view} setView={setView} copyLabel="Newsletter copy" gap={20} levels={2} drafting={!!draftingBack}
        /* The way back out of the drafting screen, before Add — the drafting
           screen draws none of its own now (chromeInBar). */
        /* NOT ON THE TABLE. The drafting state lives above this and does not
           clear when she switches view, so without this she could be looking at
           the list of pieces while the bar offered her the way back out of one she
           is not on screen with. The drafting screen's own back could never do
           that — it was inside the screen. */
        lead={view !== "table" && draftingBack && <BarBack {...draftingBack} />}
        add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add piece" onClick={() => setMenuOpen(!menuOpen)} />
          {menuOpen && (
            <Menu2 head="What are you writing?" items={NEWSLETTER_TYPES.map((t) => [
              t.id === "blog" ? "📝" : t.id === "email" ? "✉️" : "📰",
              t.label,
              t.id === "blog" ? t.hint : `${t.hint} Usually made from a blog post.`,
              false,
              () => { setMenuOpen(false); onAdd(t.id); },
            ])} />
          )}
        </div>
      } />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your newsletters didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}

      {children}
    </>
  );
}


/* ── Table view ───────────────────────────────────────────────────────────── */
export const NEWSLETTER_COLS = "28px 1.7fr 1.2fr .9fr 1.2fr .6fr .6fr .8fr .8fr 34px";

export function NewsletterTable({ newsletters = [], blocksFor, loading, onOpen, onDelete, onReorder }) {
  return (
    <>
      <L1Table
        groups={groupNewsletters(newsletters)} cols={NEWSLETTER_COLS}
        labels={["Piece", "Type", "Pillar", "Sections", "Words", "Read", "Updated", "Status", ""]}
        loading={loading} loadingText="Loading your newsletters…"
        empty={<>Nothing written yet. Click <b style={nb}>Add piece</b> and start with a blog post — the email and the Substack post get cut from it.</>}
        onOpen={onOpen} onDelete={onDelete} deleteTitle="Delete this piece"
        onReorder={onReorder}
        rowFor={(n) => {
          const blocks = blocksFor(n.id);
          const words = newsletterWords(blocks);
          const flag = claimsCheck(n, blocks);
          return (
            <>
              <div style={{ minWidth: 0, fontSize: 13.5, fontWeight: 600, color: n.name ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {newsletterLabel(n)}
              </div>
              <div style={{ minWidth: 0 }}><NewsletterTypePill newsletter={n} newsletters={newsletters} /></div>
              {/* Amber when a blog post has none — it is the one thing on this
                  row that costs her nothing today and everything in a search
                  result. */}
              <div style={{ minWidth: 0, fontSize: 12.5, color: n.pillar ? B.mid : (flag ? B.amber : B.ghost), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {n.pillar || (isSource(n) ? "No pillar" : "—")}
              </div>
              <StateDots noun="section"
                states={blocks.map(blockState)}
                titles={blocks.map((b) => `${blockName(b)} — ${blockWords(b) ? `${blockWords(b)} words` : "not written"}`)} />
              <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>{words || "—"}</div>
              <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, whiteSpace: "nowrap" }}>{words ? `${readMinutes(blocks)} min` : "—"}</div>
              <div style={{ fontSize: 12, color: B.faint, whiteSpace: "nowrap" }}>{n.updatedAt ? timeAgo(n.updatedAt) : "—"}</div>
              <div><StateTag state={newsletterState(n, blocks)} /></div>
            </>
          );
        }} />
      {newsletters.length > 0 && (
        <Note><b style={nb}>Open a piece to write it.</b> Drag by the grip to put them in your order.</Note>
      )}
    </>
  );
}


/* ── Newsletter copy ──────────────────────────────────────────────────────────
   The pieces down the side, banded like the table, the one she is writing
   beside them. The dot marks it Done, like a page's or a script's. */
export function NewsletterCopy({ newsletters = [], blocksFor, selectedId, onSelect, onReorder, onMark, drafting, onExitToTable, children }) {
  const groups = groupNewsletters(newsletters);
  const ordered = groups.flatMap((g) => g.rows);
  return (
    <RecordCopy
      bare={drafting}
      /* TWO LEVELS: the Table is this list, so a phone skips the list screen
         and comes straight back here. See `levels` in shell.jsx. */
      levels={2} onExit={onExitToTable} exitLabel="All newsletters"
      records={ordered} selectedId={selectedId} onSelect={onSelect} onMark={onMark}
      /* A drag is turned back into a move inside its own band; a drop onto
         another band is ignored rather than silently changing the type, which
         would strand the sections that belong to it. */
      onReorder={(from, to) => {
        const a = ordered[from];
        const b = ordered[to];
        if (!a || !b || (a.type || "blog") !== (b.type || "blog")) return;
        const band = groups.find((g) => g.rows.includes(a));
        onReorder?.(band.key, band.rows.indexOf(a), band.rows.indexOf(b));
      }}
      noun="piece"
      emptyHint={<>Nothing written yet. Click <b style={nb}>Add piece</b> above to start one.</>}
      rowFor={(n) => {
        const blocks = blocksFor(n.id);
        const state = newsletterState(n, blocks);
        const words = newsletterWords(blocks);
        return {
          title: newsletterLabel(n),
          meta: [typeDef(n.type).label, cutFromLabel(n, newsletters), words ? `${words} words` : null].filter(Boolean).join(" · "),
          dot: B[STATUS_DOT[state]],
          dotTitle: NEWSLETTER_STATE_LABEL[state],
          markable: state !== "empty",
          done: state === "done",
        };
      }}>
      {children}
    </RecordCopy>
  );
}


/* ── One piece ────────────────────────────────────────────────────────────────
   Its name is the heading. What GHL's blog API wants lives behind the cog. */
export function NewsletterPiece({
  newsletter, newsletters = [], blocks = [], offers = [], pillars = [], error,
  onSave, onDelete, onSaveBlock, onRemoveBlock, onAddBlock, onReorderBlocks,
  onDraftBlock, onWrite, onSparkleBlock, sparklingBlockId = null, draftedBlock = null,
  onMakeVariant, makingVariant = null, onCopyOut, brand = {}, onPublish = null,
}) {
  const [name, setName] = useState(newsletter?.name || "");
  const [cog, setCog] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);   // { ok, text }

  if (!newsletter) return null;

  const state = newsletterState(newsletter, blocks);
  const words = newsletterWords(blocks);
  const flag = claimsCheck(newsletter, blocks);
  const offer = offers.find((o) => o.id === newsletter.offerId);
  const set = (k, v) => onSave({ ...newsletter, [k]: v });
  const canAdd = addableBlockKinds(blocks, newsletter.type || "blog");
  /* The cuts already made, so a button says "Make the email" once and offers
     "Another email" after that rather than looking like it did nothing. */
  const made = new Set((newsletters || []).filter((n) => n.sourceId === newsletter.id).map((n) => n.type));

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <NewsletterTypePill newsletter={newsletter} newsletters={newsletters} />
        {state === "empty" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: B.mid }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: B[STATUS_DOT[state]] }} />
            {NEWSLETTER_STATE_LABEL[state]}
          </span>
        ) : (
          <SectionStatus value={newsletter.status === "done" ? "done" : "draft"} onChange={(status) => set("status", status)} />
        )}
        <span style={{ fontSize: 11.5, color: B.faint }}>{words ? `${words} words · ${readMinutes(blocks)} min read` : "Nothing written yet"}</span>
        <SavedTick problem={error ? "That didn't save — try again" : ""} />
        {onWrite && (
          <span style={{ marginLeft: "auto", display: "flex" }}>
            <ChatWithBrill label={`Write this ${isSource(newsletter) ? "post" : typeDef(newsletter.type).label.toLowerCase()} with Brill`} onClick={onWrite} />
          </span>
        )}
        <button onClick={() => setCog(!cog)} title="Settings for this piece" aria-label="Newsletter settings" aria-expanded={cog}
          style={{ ...btnGhost, marginLeft: onWrite ? 0 : "auto", padding: "7px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, color: cog ? B.teal : B.mid, borderColor: cog ? B.tealLine : B.border2 }}>
          <Settings size={13} /> Settings
        </button>
      </div>

      <div style={{ display: "flex", marginBottom: 14 }}>
        <PageTitleField value={name} onChange={setName}
          placeholder={isSource(newsletter) ? "Title this post" : `Name this ${typeDef(newsletter.type).label.toLowerCase()}`}
          label="Title"
          onCommit={() => { if (name !== (newsletter.name || "")) set("name", name); }} />
      </div>

      {cog && (
        <NewsletterSettings newsletter={newsletter} offers={offers} pillars={pillars}
          onSave={(patch) => { onSave({ ...newsletter, ...patch }); setCog(false); }}
          onClose={() => setCog(false)} />
      )}

      <button onClick={() => setCog(true)} title="Change these in settings"
        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, marginBottom: 14, padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
        <span style={{ color: newsletter.pillar ? B.mid : B.ghost }}>{newsletter.pillar || "No pillar picked"}</span>
        <span style={{ color: B.faint }}>·</span>
        <span style={{ color: offer ? B.mid : B.ghost }}>{offer ? `Points at ${offer.name || "an untitled offer"}` : "No offer connected"}</span>
        {isSource(newsletter) && (
          <>
            <span style={{ color: B.faint }}>·</span>
            <span style={{ color: newsletter.canonicalUrl ? B.mid : B.ghost }}>{newsletter.canonicalUrl || "No address yet"}</span>
          </>
        )}
      </button>

      {/* THE PICTURE, ON THE PIECE (her ask, 2026-11-03): it lived only in
          the cog, where she would never find it. The same stored image the
          cog edits, so the two cannot disagree. A Substack post has nowhere
          to put one. */}
      {(isSource(newsletter) || newsletter.type === "email") && (
        <PiecePicture key={newsletter.id} newsletter={newsletter} onSave={(patch) => onSave({ ...newsletter, ...patch })}
          hint={isSource(newsletter) ? "Beside the title on the blog, and the preview card when it's shared. Upright pictures fit best." : "Sits at the top of the designed email, under your logo."} />
      )}

      {/* THE CHECK SHE ASKED FOR, said as what it costs her. It is a note, not
          a gate: she can publish a pure story and ignore this forever. */}
      {flag && (
        <div style={{ background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 12.5, color: B.text, lineHeight: 1.6 }}>
          <b style={{ color: B.amber }}>Nothing quotable in here yet.</b> {flag.text}
        </div>
      )}

      {blocks.length === 0 ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 26, textAlign: "center", color: B.dim, fontSize: 13, lineHeight: 1.6 }}>
          Nothing in this piece yet. Click <b style={nb}>Add section</b> — the opening is the usual place to start.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SortableList ids={blocks.map((b) => b.id)} onReorder={(from, to) => onReorderBlocks?.(newsletter.id, from, to)}>
            {blocks.map((b) => (
              <SortableItem key={b.id} id={b.id}>
                {({ handle, dragging }) => (
                  <NewsletterBlock
                    /* KEYED BY THE DRAFT STAMP — a Block holds its own copy of
                       the content from the moment it mounts, so without this
                       the sparkle saves the words and the row still reads
                       "Not written yet". Same as every other panel. */
                    key={`${b.id}:${draftedBlock?.blockId === b.id ? draftedBlock.n : 0}`}
                    block={b} onSave={onSaveBlock} onRemove={onRemoveBlock}
                    startOpen={draftedBlock?.blockId === b.id}
                    onChat={onDraftBlock ? () => onDraftBlock(b) : null}
                    sparkle={onSparkleBlock ? {
                      busy: sparklingBlockId === b.id,
                      onClick: () => onSparkleBlock(b),
                      label: blockWords(b) ? "Rewrite with Brill" : "Write with Brill",
                    } : null}
                    dragHandle={handle} dragging={dragging} />
                )}
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
        <div style={{ position: "relative", display: "inline-block" }}>
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>+ Add section</button>
          {menuOpen && (
            <Menu2 head="What does this piece need?" items={canAdd.map((k) => [
              "📝", k.label, k.hint, false, () => { setMenuOpen(false); onAddBlock?.(newsletter, k.id); },
            ])} />
          )}
        </div>
        {onCopyOut && words > 0 && newsletter.type !== "email" && (
          <button onClick={() => onCopyOut(newsletterToText(newsletter, blocks))} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
            Copy the whole thing
          </button>
        )}
      </div>

      {/* AN EMAIL GOES OUT PLAIN OR DESIGNED (her call, 2026-10-12) — Gmail,
          Mailchimp, Kit or GHL, from the same words. */}
      {newsletter.type === "email" && words > 0 && (
        <EmailCopy where="newsletter" defaultFormat="designed" parts={emailParts(newsletter, blocks)} brand={brand} ghlName={newsletterLabel(newsletter)} handoff="campaign"
          picture={newsletter.imageUrl ? { url: newsletter.imageUrl, alt: newsletter.imageAlt || "" } : null} />
      )}

      {/* PUBLISH TO HER BLOG (2026-11-03). Admin only: `onPublish` is handed
          in only for her. The server builds the page from the post as saved,
          so it says what is on screen once the last field has saved. */}
      {isSource(newsletter) && words > 0 && onPublish && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${B.border}` }}>
          <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 10, lineHeight: 1.6 }}>
            {isOnBlog(newsletter)
              ? <>On your blog at <a href={newsletter.canonicalUrl} target="_blank" rel="noreferrer" style={{ color: B.teal }}>{newsletter.canonicalUrl.replace(/^https:\/\//, "")}</a>. Changes here go live when you update it.</>
              : "Put this post on blog.tevrawood.com. It goes live about a minute after you press."}
          </div>
          <button disabled={publishing} onClick={async () => {
            setPublishing(true);
            setPublished(null);
            const r = await onPublish(newsletter);
            setPublishing(false);
            setPublished(r.ok
              ? { ok: true, text: `${r.updated ? "Updated" : "Published"}. Live at ${r.url.replace(/^https:\/\//, "")} in about a minute.` }
              : { ok: false, text: r.error });
          }} style={{ ...btnPrimary, padding: "9px 15px", fontSize: 12.5, opacity: publishing ? 0.6 : 1 }}>
            {publishing ? "Publishing…" : isOnBlog(newsletter) ? "Update on the blog" : "Publish to blog"}
          </button>
          {published && (
            <div role="status" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5, color: published.ok ? B.dim : B.amber }}>{published.text}</div>
          )}
        </div>
      )}

      {/* MAKE A CUT. Only from a source, only once it has words — cutting an
          empty post is cutting nothing. Teal, because it is the action she
          came here for (rule 5). */}
      {isSource(newsletter) && words > 0 && onMakeVariant && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${B.border}` }}>
          <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 10, lineHeight: 1.6 }}>
            Cut this post for somewhere else. Brill writes the cut from this piece — a different
            opening, not the same paragraphs with sentences taken out.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {VARIANT_TYPES.map((t) => (
              <button key={t} disabled={makingVariant === t} onClick={() => onMakeVariant(newsletter, t)}
                style={{ ...(made.has(t) ? btnGhost : btnPrimary), padding: "9px 15px", fontSize: 12.5, opacity: makingVariant === t ? 0.6 : 1 }}>
                {makingVariant === t ? "Making…" : made.has(t) ? `Another ${typeDef(t).label.toLowerCase()}` : `Make the ${typeDef(t).label.toLowerCase()}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <RecordFooter tick={false} onDelete={() => onDelete(newsletter)} />
    </>
  );
}


/* ── The piece's one picture ─────────────────────────────────────────────────
   Nothing to press (rule 1): a pick saves at once, the description as she
   leaves the field. Every action stays on the row, picture or not (rule 3). */
function PiecePicture({ newsletter, onSave, hint }) {
  const [alt, setAlt] = useState(newsletter?.imageAlt || "");
  const url = newsletter?.imageUrl || "";
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start", border: `1px solid ${B.border}`, borderRadius: 10, padding: 12, marginBottom: 16 }}>
      {url ? (
        <img src={url} alt="" style={{ width: 72, height: 90, objectFit: "cover", borderRadius: 7, flex: "none", background: B.surface2 }} />
      ) : (
        <div aria-hidden="true" style={{ width: 72, height: 90, borderRadius: 7, flex: "none", border: `1px dashed ${B.border2}`, display: "flex", alignItems: "center", justifyContent: "center", color: B.ghost, fontSize: 11 }}>No picture</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: B.text }}>Picture</span>
          <MediaPickButton remember="newsletter" label={url ? "Change picture" : "Choose picture"}
            onPick={(picked) => onSave({ imageUrl: picked || null })} />
          {url && (
            <button onClick={() => onSave({ imageUrl: null })}
              style={{ border: "none", background: "none", padding: "4px 2px", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: B.mid, textDecoration: "underline" }}>
              Remove
            </button>
          )}
        </div>
        <input value={alt} onChange={(e) => setAlt(e.target.value)}
          onBlur={() => { if (alt.trim() !== (newsletter?.imageAlt || "")) onSave({ imageAlt: alt.trim() || null }); }}
          placeholder="What the picture shows, for people who can't see it" aria-label="What the picture shows"
          style={{ ...input, marginBottom: 6 }} />
        <div style={{ fontSize: 11.5, color: B.faint, lineHeight: 1.5 }}>{hint}</div>
      </div>
    </div>
  );
}


/* ── Settings, as a pop-up ────────────────────────────────────────────────────
   Outreach's pattern (rule 8): a draft, written on Save, so Cancel means
   something. THE TYPE IS NOT HERE — changing it would strand the sections that
   belong to the old one (an email's subject line on a blog post), and a cut
   pointing at a source that is now a cut itself is nonsense. A different type
   is a different piece.

   The blog keeps the fields GHL's blog API asks for. An email does not: a slug
   and a summary card mean nothing in an inbox. */
export function NewsletterSettings({ newsletter, offers = [], pillars = [], onSave, onClose }) {
  const source = isSource(newsletter);
  const [draft, setDraft] = useState({
    pillar: newsletter?.pillar || "",
    offerId: newsletter?.offerId || null,
    description: newsletter?.description || "",
    urlSlug: newsletter?.urlSlug || "",
    canonicalUrl: newsletter?.canonicalUrl || "",
    imageUrl: newsletter?.imageUrl || "",
    imageAlt: newsletter?.imageAlt || "",
    tags: (Array.isArray(newsletter?.tags) ? newsletter.tags : []).join(", "),
    categories: (Array.isArray(newsletter?.categories) ? newsletter.categories : []).join(", "),
  });
  const put = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const list = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Newsletter settings" onClick={(e) => e.stopPropagation()}
        style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: "20px 20px 16px", width: "100%", maxWidth: 520, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 16 }}>{newsletterLabel(newsletter)}</div>

        {/* THE PILLAR IS HER OWN WORDS, read from her locked visibility work —
            never a list invented here. With nothing locked yet she can still
            type one, rather than being sent back to the Accelerator. */}
        <Setting label="Content pillar"
          hint={pillars.length
            ? "Every post reinforces one of your three. That repetition is what makes it land."
            : "Lock your content pillars in My Visibility and they'll show up here to pick from."}>
          {pillars.length > 0 ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {pillars.map((p) => {
                const on = draft.pillar === p;
                return (
                  <button key={p} onClick={() => put("pillar", on ? "" : p)} aria-pressed={on}
                    style={{ padding: "7px 13px", borderRadius: 18, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${on ? B.tealLine : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.mid, fontWeight: on ? 700 : 500 }}>
                    {p}
                  </button>
                );
              })}
            </div>
          ) : (
            <input value={draft.pillar} onChange={(e) => put("pillar", e.target.value)} placeholder="What this piece is about" style={input} />
          )}
        </Setting>

        <Setting label="Offer it points at" hint="What the ask at the end sends them to. Optional.">
          <select value={draft.offerId || ""} onChange={(e) => put("offerId", e.target.value || null)} style={input}>
            <option value="">No offer</option>
            {offers.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
          </select>
        </Setting>

        {source && (
          <>
            <Setting label="The summary" hint="The line that shows in a search result or a preview card. Two sentences at most.">
              <textarea value={draft.description} onChange={(e) => put("description", e.target.value)} rows={2}
                placeholder="What someone gets from reading this" style={{ ...input, resize: "vertical", lineHeight: 1.55 }} />
            </Setting>
            <Setting label="Its address" hint="The end of the URL, then the full address once it's live. Both are what GHL asks for when you publish.">
              <input value={draft.urlSlug} onChange={(e) => put("urlSlug", e.target.value)} placeholder="why-coaching-feels-unsustainable"
                style={{ ...input, marginBottom: 8 }} />
              <input value={draft.canonicalUrl} onChange={(e) => put("canonicalUrl", e.target.value)} placeholder="https://blog.yoursite.com/…" style={input} />
            </Setting>
            <Setting label="Categories and tags" hint="Commas between them. They go straight across when you publish.">
              <input value={draft.categories} onChange={(e) => put("categories", e.target.value)} placeholder="Pricing, Positioning" style={{ ...input, marginBottom: 8 }} />
              <input value={draft.tags} onChange={(e) => put("tags", e.target.value)} placeholder="group coaching, burnout" style={input} />
            </Setting>
          </>
        )}

        {/* THE PICTURE. A blog post's image is its card; an email's is the
            HEADER of the designed email (her call, 2026-10-12 — a Canva banner
            comes through the same picker, saved into GHL first so its link
            lasts). A Substack post has nowhere to put one. */}
        {(source || newsletter?.type === "email") && (
          <Setting label={source ? "The image" : "Header picture"}
            hint={source ? "A link, or pick one from your library." : "Sits at the top of the designed email, under your logo. Optional."}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <input value={draft.imageUrl} onChange={(e) => put("imageUrl", e.target.value)} placeholder="https://…" style={{ ...input, flex: 1 }} />
              <MediaPickButton remember="newsletter" label="Pick" onPick={(url) => put("imageUrl", url)} />
            </div>
            <input value={draft.imageAlt} onChange={(e) => put("imageAlt", e.target.value)} placeholder="What the image shows" style={input} />
          </Setting>
        )}

        {!source && (
          <Setting label="The full version" hint="Where the long post lives, so this can point back at it.">
            <input value={draft.canonicalUrl} onChange={(e) => put("canonicalUrl", e.target.value)} placeholder="https://blog.yoursite.com/…" style={input} />
          </Setting>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "9px 16px", fontSize: 12.5 }}>Cancel</button>
          <button onClick={() => onSave({
            ...draft,
            pillar: draft.pillar.trim() || null,
            description: draft.description.trim() || null,
            urlSlug: draft.urlSlug.trim() || null,
            canonicalUrl: draft.canonicalUrl.trim() || null,
            imageUrl: draft.imageUrl.trim() || null,
            imageAlt: draft.imageAlt.trim() || null,
            tags: list(draft.tags),
            categories: list(draft.categories),
          })} style={{ ...btnPrimary, padding: "9px 16px", fontSize: 12.5 }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Setting({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}


/* ── One section ──────────────────────────────────────────────────────────────
   The shared Block, same as a website section and a script's. */
export function NewsletterBlock({ block, onSave, onRemove, onChat, sparkle = null, startOpen = false, dragHandle, dragging }) {
  const kind = blockKindOf(block);
  const def = blockKindDef(block?.blockType);
  return (
    <Block
      kind={kind} noun="section"
      content={block?.content || {}}
      words={(c) => blockWords({ ...block, content: c })}
      onCommit={(c) => onSave({ ...block, content: c })}
      onChat={onChat || undefined}
      sparkle={sparkle || undefined}
      startOpen={startOpen}
      onClear={() => onRemove?.(block)}
      clearLabel={`Remove ${blockName(block).toLowerCase()}`}
      dragHandle={dragHandle} dragging={dragging}
      editTop={
        <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.55, marginBottom: 14 }}>
          <BlockNameField block={block} onSave={onSave} placeholder={def.id === "custom" ? "Name this section" : def.label} />
          {def.hint}.
        </div>
      }
    />
  );
}

/* Her name for the section, written on the way out. */
function BlockNameField({ block, onSave, placeholder }) {
  const [value, setValue] = useState(block?.name || "");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>Section name</span>
      <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} aria-label="Section name"
        onBlur={() => { if (value !== (block?.name || "")) onSave({ ...block, name: value }); }}
        style={{ ...input, width: "auto", flex: 1, padding: "6px 9px", fontSize: 12.5 }} />
    </div>
  );
}


/* ── Writing with Brill ───────────────────────────────────────────────────────
   COST: one API call per message — NEW SPEND with this panel. Her locked work
   rides along scoped by CONTEXT_SCOPES.newsletter_copy; the whole-piece scope
   sends every section too, and a CUT also sends the source it came from, which
   is the largest context this panel builds. */
export async function newsletterDraftReply({
  token, newsletter, block, blocks = [], offers = [], source = null, sourceBlocks = [],
  pillars = [], chatContext = {}, messages, whole = false,
}) {
  const ctx = buildMomentumContext({
    moduleId: "newsletter_copy",
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (id, name) => {
      const stamp = chatContext.outputStamps?.[id]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const args = { newsletter, blocks, offers, source, sourceBlocks, pillars };
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "newsletter_copy",
      max_tokens: whole ? 4000 : 1800,
      moduleLabel: typeDef(newsletter?.type).label,
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        { role: "user", content: whole ? buildNewsletterContext(args) : buildNewsletterBlockContext({ ...args, block }) },
        ...messages,
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Something went wrong reaching Brill — try again in a moment. Nothing in this piece was touched; you can always type it yourself.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}

/* Named once: the bar renders it (`draftingBack` on NewsletterShell) and
   Next's last step reads its words off the same string. */
export const PIECE_BACK = "← Back to the piece";


export function NewsletterDrafter({
  newsletter, block, blocks = [], offers = [], source = null, sourceBlocks = [],
  chatContext = {}, onSave, onBack, onGoToBlock, startScope = "block", nextRecord = null,
}) {
  /* THE CHAT IS KEPT, per piece (supabase/0049). */
  const keep = useRecordKeep({ tool: "newsletter", recordId: newsletter?.id, blockKey: block?.id, liveKeys: blocks.map((b) => b.id) });
  if (!newsletter || !block) return null;
  const kind = blockKindOf(block);
  const pillars = pillarLabels(chatContext.acceleratorOutputs?.social?.["Content Pillars"]);
  const has = (mod, name) => {
    const outputs = chatContext.acceleratorOutputs?.[mod] || {};
    return name ? !!outputs[name] : Object.keys(outputs).length > 0;
  };
  const flag = claimsCheck(newsletter, blocks);
  const contextItems = [
    { label: "Your voice", on: has("voice", "Voice Profile") },
    { label: "Your client", on: has("client", "Ideal Client Profile") },
    { label: "Your pillar", on: !!String(newsletter.pillar || "").trim() },
    ...(isSource(newsletter)
      ? [{ label: "The other sections", on: blocks.some((b) => b.id !== block.id && blockWords(b) > 0) }]
      : [{ label: "The post it's cut from", on: !!source && sourceBlocks.some((b) => blockWords(b) > 0) }]),
  ];
  const index = blocks.findIndex((b) => b.id === block.id);
  const contentOf = (b) => b.content || {};
  const args = { newsletter, blocks, offers, source, sourceBlocks, pillars };
  const noun = isSource(newsletter) ? "post" : typeDef(newsletter.type).label.toLowerCase();

  /* The claims check follows her into the chat. On the record it is a note she
     can walk past; here Brill can actually fix it, so it rides in as something
     to press rather than a second warning. */
  const laterSuggestions = ["Shorter", "Make it sound more like me", "More specific"];
  if (flag) laterSuggestions.unshift("Give me something quotable");

  return (
    <Drafter
      /* Recording the voice (supabase/0030): where a Lock lands. */
      voice={{ tool: "newsletter_copy", recordType: "newsletter_block", blockId: block?.id }}
      keep={keep}
      /* Stories, objections and ideas are BACKGROUND, never pasted (her rule) —
         the Drafter says so in the hand-off it writes. A testimonial is not
         offered here: a client's words in a public post is a decision about
         permission, and the Library's own picker is where that is visible. */
      libraryKinds={["story", "objection", "idea"]}
      fieldProps={{ usedAs: { type: "newsletter", id: newsletter?.id, label: newsletterLabel(newsletter) } }}
      kind={kind}
      content={contentOf(block)}
      onCommit={(content) => onSave({ ...block, content })}
      ask={({ token, messages, content }) => newsletterDraftReply({
        token, ...args, block: { ...block, content }, chatContext, messages,
      })}
      parse={(reply, content) => {
        const parsed = parseNewsletterBlockReply(reply, block);
        return parsed ? applyNewsletterBlockReply(content, parsed, block) : null;
      }}
      strip={stripNewsletterBlock}
      title={`Write ${blockName(block).toLowerCase()}`}
      opener={isSource(newsletter)
        ? `I'm on ${blockName(block).toLowerCase()} of ${newsletterLabel(newsletter)}. Tell me what happened or what you want to say — or say "draft it" and I'll write it from your pillar and your voice.`
        : `I'm on ${blockName(block).toLowerCase()} of this ${noun}. Say "cut it from the post" and I'll take one thread of the full version and open on it differently.`}
      suggestions={{
        first: isSource(newsletter)
          ? ["Draft it from my pillar", "I'll tell you what happened"]
          : ["Cut it from the post", "Take a different angle"],
        later: laterSuggestions,
      }}
      contextItems={contextItems}
      pager={onGoToBlock && index >= 0 ? {
        items: blocks.map((b) => ({ id: b.id, label: blockName(b), written: blockWords(b) > 0 })),
        index,
        onGo: onGoToBlock,
        noun: "Section",
      } : null}
      chromeInBar
      backLabel={PIECE_BACK} onBack={onBack}
      startScope={startScope} nextRecord={nextRecord}
      record={{
        blockLabel: "Section",
        label: typeDef(newsletter.type).label,
        noun,
        title: `Write ${newsletterLabel(newsletter)}`,
        heading: newsletterLabel(newsletter),
        opener: isSource(newsletter)
          ? `We're on the whole post — ${blocks.length} section${blocks.length === 1 ? "" : "s"}. Tell me what it's about, or say "draft it all" and I'll write every section from your pillar and your voice.`
          : `We're on the whole ${noun} — ${blocks.length} section${blocks.length === 1 ? "" : "s"}. Say "cut it from the post" and I'll write the lot from the full version, opening on a different thread.`,
        suggestions: {
          first: isSource(newsletter)
            ? ["Draft it all", "I'll tell you what it's about"]
            : ["Cut it from the post", "Take a different angle"],
          later: laterSuggestions,
        },
        blocks: blocks.map((b) => ({ id: b.id, kind: blockKindOf(b), content: contentOf(b) })),
        ask: ({ token, messages, contents }) => newsletterDraftReply({
          token, ...args, whole: true, chatContext, messages,
          blocks: blocks.map((b) => ({ ...b, content: contents[b.id] || contentOf(b) })),
        }),
        parse: (reply, contents) => {
          const byBlock = parseWholeNewsletterReply(reply, blocks);
          if (!byBlock) return null;
          const out = {};
          for (const b of blocks) {
            if (byBlock[b.id]) out[b.id] = applyNewsletterBlockReply(contents[b.id] || contentOf(b), byBlock[b.id], b);
          }
          return out;
        },
        strip: stripNewsletterBlock,
        onCommitBlock: (id, content) => {
          const b = blocks.find((x) => x.id === id);
          return b ? onSave({ ...b, content }) : null;
        },
      }}
      where={newsletterLabel(newsletter)}
    />
  );
}
